import { groupBySection, packColumns, type MenuBlock } from '../paginate.js';
import {
  bilingual,
  hasTitle,
  image,
  listColumns,
  listCss,
  listItem,
  sectionSubtitle,
  sectionTitle,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * A hand-made menu on brown paper: marker headings with a scribbled underline, dashed leaders,
 * and photos taped on like polaroids.
 */

const S = '.mb-t-kraft-paper';

// Paper fibre: fine noise plus faint streaks, tinted by the theme's background colour beneath.
const FIBRE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='300' height='300'><filter id='f'><feTurbulence type='fractalNoise' baseFrequency='.9 .25' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 .2 0 0 0 0 .12 0 0 0 0 .05 0 0 0 .18 0'/></filter><rect width='100%25' height='100%25' filter='url(%23f)'/></svg>\")";

function block(ctx: RenderContext, b: MenuBlock): string {
  const head = hasTitle(b.section)
    ? `<div class="kp-head">${sectionTitle(ctx, b.section, 'kp-title')}${b.continued ? '' : sectionSubtitle(ctx, b.section, 'kp-sub')}</div>`
    : '';
  return `<div class="kp-sec">${head}${b.items.map((i) => listItem(ctx, i, 'kp')).join('')}</div>`;
}

export const kraftPaper: MenuTemplate = {
  id: 'kraft-paper',
  name: 'Kraft paper',
  description: 'Brown paper, marker headings and taped polaroid photos, like a hand-made menu. Good for burgers, sandwiches and pizza.',
  defaultTheme: {
    backgroundColor: '#c9a37b',
    surfaceColor: '#f7f1e6',
    textColor: '#2b1d12',
    primaryColor: '#24170e',
    accentColor: '#a3201a',
    fontId: 'chalk',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'kraft', name: 'Kraft', theme: {} },
    { id: 'newsprint', name: 'Newsprint', theme: { backgroundColor: '#ece6da', textColor: '#222222', primaryColor: '#111111', accentColor: '#c1121f', fontId: 'bold' } },
    { id: 'butcher', name: 'Butcher paper', theme: { backgroundColor: '#f6f4ef', textColor: '#1b1b1b', primaryColor: '#1b1b1b', accentColor: '#d62828', fontId: 'street' } },
    { id: 'cardboard', name: 'Cardboard', theme: { backgroundColor: '#7f5f41', textColor: '#fff6e8', primaryColor: '#ffffff', accentColor: '#ffd166' } },
  ],
  cuisines: ['burgers', 'pizza'],
  uses: { featured: false, sectionImages: false, brand: true, decorations: ['First taped photo', 'Second taped photo', 'Third taped photo'] },

  paginate(board, orientation) {
    const portrait = orientation === 'portrait';
    const photos = board.decorMediaIds.filter(Boolean).length > 0;
    return packColumns(
      groupBySection(board),
      listColumns(board, {
        count: 2,
        capacity: portrait ? (photos ? 74 : 98) : 50,
        heading: 4.4,
        item: { nameEm: 1.3, descEm: 0.85, perLine: portrait ? 40 : photos ? 42 : 54, gap: 1.2 },
      }),
      { balance: true },
    );
  },

  render(ctx) {
    const { board } = ctx;
    const photos = board.decorMediaIds
      .slice(0, 3)
      .map((id, i) => {
        const img = image(ctx, id, 'kp-pic');
        return img ? `<div class="kp-polaroid kp-p${i}">${img}</div>` : '';
      })
      .join('');
    const header = [
      '<div class="kp-header">',
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'kp-heading'),
      bilingual(ctx, board.brand.name, board.brand.nameAr, 'kp-brand'),
      bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'kp-tagline'),
      '</div>',
    ].join('');
    const lists = `<div class="kp-cols">${ctx.page.columns
      .map((c) => `<div class="kp-col">${c.map((b) => block(ctx, b)).join('')}</div>`)
      .join('')}</div>`;
    return `<div class="kp-body${photos ? '' : ' kp-no-photos'}"><div class="kp-main">${header}${lists}</div>${photos ? `<div class="kp-photos">${photos}</div>` : ''}</div>`;
  },

  css: `
${S}{padding:2.6em 3.2em;background-image:${FIBRE},radial-gradient(ellipse at 50% 40%,rgba(255,255,255,.18),transparent 70%)}
${S} .kp-body{flex:1;min-height:0;display:grid;grid-template-columns:1fr 26em;gap:3em}
${S}.mb-portrait .kp-body{grid-template-columns:1fr;grid-template-rows:auto 1fr}
${S} .kp-no-photos{grid-template-columns:1fr}
${S} .kp-main{min-width:0;min-height:0;display:flex;flex-direction:column}
${S}.mb-portrait .kp-photos{order:-1}
${S} .kp-header{text-align:center;margin-bottom:1.6em}
${S} .kp-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:5em;color:var(--mb-primary);
  line-height:1;transform:rotate(-1.5deg)}
${S} .kp-heading.mb-2nd{font-size:2.8em}
${S} .kp-brand{font-family:var(--mb-font-script);font-size:1.9em;color:var(--mb-accent);margin-top:.2em;line-height:1.3}
${S} .kp-tagline{font-size:.95em;letter-spacing:.25em;text-transform:uppercase;opacity:.75;margin-top:.4em}
${S} .kp-cols{flex:1;min-height:0;display:grid;grid-template-columns:1fr 1fr;gap:3em}
${S} .kp-col{min-width:0;display:flex;flex-direction:column;gap:1.8em}
${S} .kp-head{margin-bottom:.8em}
${S} .kp-title{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:2.3em;color:var(--mb-accent);
  line-height:1.1;text-decoration:underline wavy;text-decoration-thickness:.06em;text-underline-offset:.2em}
${S} .kp-title.mb-2nd{font-size:1.6em}
${S} .kp-sub{font-family:var(--mb-font-script);font-size:1.15em;opacity:.85}
${listCss(S, 'kp', { name: '1.3em', price: '1.35em', desc: '.85em', leader: '.13em dashed color-mix(in srgb,var(--mb-text) 45%,transparent)' })}
${S} .kp-item{margin-bottom:1em}
${S} .kp-name{font-weight:800;text-transform:uppercase;letter-spacing:.03em;color:var(--mb-primary)}
${S} .kp-price{font-weight:800;color:var(--mb-accent)}
${S} .kp-photos{display:flex;flex-direction:column;justify-content:space-around;align-items:center;gap:1em;min-height:0}
${S}.mb-portrait .kp-photos{flex-direction:row;height:20em}
${S} .kp-polaroid{position:relative;background:#fbf8f1;padding:.8em .8em 2.4em;box-shadow:0 .8em 1.6em rgba(0,0,0,.28);width:22em}
${S}.mb-portrait .kp-polaroid{width:18em}
${S} .kp-polaroid:before{content:'';position:absolute;top:-1em;left:50%;width:7em;height:2em;transform:translateX(-50%) rotate(-3deg);
  background:rgba(255,248,220,.75);box-shadow:0 .1em .3em rgba(0,0,0,.1)}
${S} .kp-p0{transform:rotate(-4deg)}
${S} .kp-p1{transform:rotate(3deg)}
${S} .kp-p2{transform:rotate(-2deg)}
${S} .kp-pic{display:block;width:100%;aspect-ratio:4/3;object-fit:cover}
`,
};
